import React, {useEffect, useMemo, useState} from 'react';
import {WebSnippetProfile, WalletDownloadSnippetProfile} from "../Helpers/Types";
import {useSnippetContext} from "../Utils/UseSnippetContext";
import useServiceUrls from "../Utils/UseServiceUrls";
import {getParameterCaseInsensitive, isStringHtml} from "../Helpers/Functions";
import SnippetHeader from "../Helpers/SnippetHeader";
import SnippetFooter from "../Helpers/SnippetFooter";
import QRCode from "react-qr-code";
import {WalletButtons} from "../Helpers/WalletButtons";
import AddToWalletButton from "../components/AddToWalletButton";
import WalletDownloadView from "../components/organisms/WalletDownloadView";
import {WalletCoupon as UICoupon} from "../components/atoms/WalletCouponCard";
import useWalletType from "../Hooks/useWalletType";
import {track} from "../Utils/Analytics";
import useWalletCoupons from "../Utils/UseWalletCoupons";
import { Settings } from '../Settings';

type WalletDownloadSnippetProps = {
    snippetProfile: WebSnippetProfile
}

type WalletCoupon = UICoupon;

const isAppleDevice = (ua: string) => {
    const lower = ua.toLowerCase();
    // iOS or iPadOS; also treat iPhone simulator on Mac as mobile Safari
    return /iphone|ipad|ipod/.test(lower) || (/macintosh/.test(lower) && /mobile\//.test(lower));
}

const isAndroid = (ua: string) => /android/.test(ua.toLowerCase());

export default function WalletDownloadSnippet(props: WalletDownloadSnippetProps) {
    const profile: WalletDownloadSnippetProfile | undefined = props.snippetProfile.WalletDownload;
    const [engagementToken, setEngagementToken] = useState<string>('');
    const [downloaded, setDownloaded] = useState<boolean>(false);
    const [isSuccessHtml, setIsSuccessHtml] = useState<boolean>(false);
    const [badToken, setBadToken] = useState<boolean>(false);
    const [coupons, setCoupons] = useState<Array<WalletCoupon>>([]);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    // @ts-ignore
    const { domain, deviceCode } = useSnippetContext();
    const { serviceUrls } = useServiceUrls(domain);
    

    const appleUrl = useMemo(() => engagementToken ? `${Settings.WalletDownloadUrl}/Apple/${domain}/${deviceCode}/${engagementToken}${Settings.SiteType === 1 ? '?bL_customDomainUrl=https://domain-azuretest.bloyal.io' : ''}` : '', [domain, deviceCode, engagementToken]);
    const googleUrl = useMemo(() => engagementToken ? `${Settings.WalletDownloadUrl}/Google/${domain}/${deviceCode}/${engagementToken}${Settings.SiteType === 1 ? '?bL_customDomainUrl=https://domain-azuretest.bloyal.io' : ''}` : '', [domain, deviceCode, engagementToken]);

    const preferredUrl = useMemo(() => {
        if (!profile) return appleUrl || googleUrl;
        if (profile.EnableAppleWallet) return appleUrl || googleUrl;
        return googleUrl || appleUrl;
    }, [profile, appleUrl, googleUrl]);

    const showApple = (profile?.EnableAppleWallet !== false) && appleUrl !== '';
    const showGoogle = (profile?.EnableGoogleWallet !== false) && googleUrl !== '';

    useEffect(() => {
        const isHtml = isStringHtml(props.snippetProfile.OnSuccessMessage)
        setIsSuccessHtml(isHtml)
    }, [props.snippetProfile.OnSuccessMessage])

    useEffect(() => {
        const parsed = require('query-string').parse(window.location.search);
        const token = getParameterCaseInsensitive(parsed, 'bl_et') ?? getParameterCaseInsensitive(parsed, 'engagementtoken');
        if (!token) {
            setBadToken(true)
        } else {
            setEngagementToken(token)
        }

        const status = getParameterCaseInsensitive(parsed, 'bl_ws') ?? getParameterCaseInsensitive(parsed, 'status');
        if (status && `${status}`.toLowerCase() === 'success')
            setDownloaded(true);

        // Optional: accept coupons passed via "bl_coupons" query parameter as JSON array
        try {
            const raw = getParameterCaseInsensitive(parsed, 'bl_coupons');
            if (raw) {
                const parsedCoupons = JSON.parse(raw);
                if (Array.isArray(parsedCoupons)) setCoupons(parsedCoupons);
            }
        } catch (e) {
            // ignore bad input
        }

        // Listen for external injection of coupons at runtime
        const handler = (e: any) => {
            if (e && e.detail && Array.isArray(e.detail)) setCoupons(e.detail);
        }
        window.addEventListener('bl:setWalletCoupons', handler as EventListener);
        return () => window.removeEventListener('bl:setWalletCoupons', handler as EventListener);
    }, []);

    // Fetch coupons from Loyalty Engine using engagement token
    const { walletCoupons } = useWalletCoupons(serviceUrls, domain, deviceCode, engagementToken);
    useEffect(() => {
        if (walletCoupons && Array.isArray(walletCoupons)) {
            const mapped: Array<WalletCoupon> = walletCoupons.map((wc: any) => {
                const coupon = wc?.Coupon ?? {};
                const profile = wc?.Profile ?? {};
                const title = coupon.Name ?? '';
                const description = coupon.Description ?? coupon.WebDescription ?? '';
                const icon = profile?.Apple?.Images?.Icon || profile?.Google?.Images?.LogoUri || coupon.ImageUrl || undefined;
                return {
                    Name: title,
                    Description: description || undefined,
                    IconUrl: icon || undefined,
                    Code: coupon.Code || undefined
                } as WalletCoupon;
            });
            setCoupons(mapped);
        }
    }, [walletCoupons]);

    useEffect(() => {
        if (!profile?.AutoDetectAndDownload || !engagementToken) return;
        const ua = navigator.userAgent || '';
        const startSpinner = () => {
            setIsDownloading(true);
            window.setTimeout(() => setIsDownloading(false), 4000);
        };
        if (profile.EnableAppleWallet && isAppleDevice(ua)) {
            if (appleUrl) { startSpinner(); window.location.assign(appleUrl); }
        } else if (profile.EnableGoogleWallet && isAndroid(ua)) {
            if (googleUrl) { startSpinner(); window.location.assign(googleUrl); }
        }
        // Otherwise, fall back to rendering the page
    }, [profile, engagementToken, appleUrl, googleUrl]);

    const onDownload = (walletType: 'apple' | 'google') => (e: any) => {
        // Allow navigation to hosted URL; mark downloaded after click so we can show message when they return
        // Some environments may open in same tab and not return; this still updates UI immediately
        setDownloaded(true);
        if (walletType === 'apple') track({ name: 'wallet.click.apple' });
        if (walletType === 'google') track({ name: 'wallet.click.google' });
    }

    // Detection copy and analytics
    const walletType = useWalletType();
    useEffect(() => {
        console.log('walletType', walletType);
        if (walletType?.type) track({ name: 'wallet.detected', type: walletType.type });
    }, [walletType?.type]);

    return (
        <div className='bLoyal-center' style={{padding: '24px 10px'}}>
            <SnippetHeader logoUrl={props.snippetProfile.LogoUrl} title={props.snippetProfile.Title} message={props.snippetProfile.Message} snippetCode={props.snippetProfile.Code}/>

            <WalletDownloadView
                showApple={showApple}
                showGoogle={showGoogle}
                appleUrl={appleUrl}
                googleUrl={googleUrl}
                preferredUrl={preferredUrl}
                showQRCode={profile?.DisplayQRCode !== false}
                coupons={coupons}
                envCopy={{ iosChrome: !!walletType?.isAppleOther, unknown: !!walletType?.isUnknown }}
                onUnsupported={() => track({ name: 'wallet.fail.unsupported' })}
                autoDetectAndDownload={!!profile?.AutoDetectAndDownload}
                isDownloading={isDownloading}
                downloadingMessage={profile?.DownloadingMessage || 'Starting download…'}
                onAppleDownload={() => { setIsDownloading(true); window.setTimeout(() => setIsDownloading(false), 4000); track({ name: 'wallet.click.apple' }); }}
                onGoogleDownload={() => { setIsDownloading(true); window.setTimeout(() => setIsDownloading(false), 4000); track({ name: 'wallet.click.google' }); }}
            />

            {/* Success / Error messages */}
            {downloaded && !isSuccessHtml && <p className='bl-snippet-success'>{props.snippetProfile.OnSuccessMessage}</p>}
            {downloaded && isSuccessHtml && <p className='bl-snippet-success' dangerouslySetInnerHTML={{__html: props.snippetProfile.OnSuccessMessage}}/>}
            {badToken && <p className='bLoyal-error-text'>Missing or invalid engagement token. Expected query parameter "bl_et".</p>}

            <SnippetFooter footer={props.snippetProfile.Footer} snippetCode={props.snippetProfile.Code}/>
        </div>
    )
}


